import { toE164 } from '~/lib/phone';
import {
  canonicalEnumValue,
  CSV_LIST_SEPARATOR,
  keyColumns,
  joinList,
  splitList,
  type CsvField,
  type CsvSchema
} from './schema';
import { type CellError, type ValidatedRow, type ValidationResult } from './validate';

// Reconciles an uploaded file against existing records. Kept pure so it can be tested directly.

export type DiffClassification = 'added' | 'removed' | 'updated' | 'unchanged';

/** One field that differs between an uploaded row and the record it matched. */
export type FieldChange = {
  key: string;
  label: string;
  from: string;
  to: string;
};

export type DiffChange = {
  classification: DiffClassification;
  /** Normalised identity built from every key field; opaque, so render `values` or `current`. */
  key: string;
  /** Line in the uploaded file. Absent for removals, which come from current data. */
  line?: number;
  /** Values as uploaded. Absent for removals. */
  values?: Record<string, string>;
  /** The record this matched. Absent for additions. */
  current?: Record<string, string>;
  /** Only the fields that actually differ. Present on updates. */
  fieldChanges?: FieldChange[];
  /** Why the row cannot be imported yet. The row keeps its classification; nothing commits while any remain. */
  errors?: CellError[];
  /** The earlier line this row repeats exactly; only one copy is imported (see selectedChanges). */
  repeatOf?: number;
  /** Other lines naming the same record with different content; see errorsThatBlock. */
  contestedBy?: number[];
};

export type DiffResult = {
  changes: DiffChange[];
};

export type DiffOptions = {
  /**
   * Offer records absent from the upload for deletion. Required, so every caller decides:
   * on a partial file it would propose deleting every record the file left out.
   */
  treatAbsentAsRemoval: boolean;
  /** Leave a row that matches an existing record as it is, whatever else it says. */
  addOnly?: boolean;
};

/** Collapses the differences in how a value can be written but not what it means. */
const normalise = (field: CsvField, raw: string): string => {
  const value = raw.trim();
  if (value === '') {
    return '';
  }

  const one = (part: string): string => {
    switch (field.type) {
      case 'phone':
        // E.164 is what the server stores. Invalid numbers and numbers with an
        // extension (which E.164 would drop) are compared as written.
        return toE164(part) ?? part.replace(/\s+/g, ' ');
      case 'email':
        // Case differences in an email are not a change.
        return part.replace(/\s+/g, ' ').toLowerCase();
      case 'enum':
        // Matched as validation matches it, so "internal" equals INTERNAL.
        return canonicalEnumValue(field, part) ?? part.replace(/\s+/g, ' ');
      case 'string':
      case 'number':
      case 'boolean':
      case 'date':
        // Compared as written; the server settles these on save.
        return part.replace(/\s+/g, ' ');
    }
  };

  if (field.compareLiterally) {
    return field.multiple ? splitList(value).join(CSV_LIST_SEPARATOR) : value;
  }
  if (!field.multiple) {
    return one(value);
  }
  // Compared as a set: reordering a list is not a change.
  return splitList(value)
    .map((part) => one(part))
    .sort()
    .join(CSV_LIST_SEPARATOR);
};

/** "Line 4", or "Lines 4 and 7" — for a message a user reads in place. */
const listLines = (lines: number[]): string =>
  lines.length === 1
    ? `Line ${lines[0]}`
    : `Lines ${lines.slice(0, -1).join(', ')} and ${lines[lines.length - 1]}`;

/**
 * A record's identity under `schema`. Consumers applying a change set must match with
 * this rather than recomputing it. Empty when every key field is blank, so blank rows
 * match nothing.
 */
export const recordKey = (schema: CsvSchema, record: Record<string, string>): string => {
  const parts = keyColumns(schema).map((column) => {
    const field = schema.fields.find((candidate) => candidate.key === column);
    if (!field) {
      throw new Error(`Schema keyColumn "${column}" is not one of its fields`);
    }
    return normalise(field, record[field.key] ?? '');
  });
  return parts.every((part) => part === '') ? '' : parts.join('\u0000');
};

/** Key fields as a reader knows them, for a message about the key: "A, B and C". */
const keyFieldLabels = (schema: CsvSchema): string => {
  const labels = keyColumns(schema).map(
    (column) => schema.fields.find((field) => field.key === column)?.label ?? column
  );
  return labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/** What a row sharing another's key is told, including how to tell the two apart. */
const keyClashMessage = (schema: CsvSchema, others: string): string => {
  const tieBreaker = schema.fields.find((field) => field.key === schema.tieBreaker);
  return `${others} ${others.includes(' and ') ? 'have' : 'has'} the same ${keyFieldLabels(schema)}, so only one of them can be imported. Skip the ones you don't want${
    tieBreaker
      ? `, or change the ${tieBreaker.label.toLowerCase()} on one of them to keep them all`
      : ''
  }.`;
};

/** A row's values as they would be written over `current`: add-only lists are merged in. */
const valuesAfterMerge = (
  schema: CsvSchema,
  uploaded: Record<string, string>,
  current: Record<string, string>
): Record<string, string> => {
  const values = { ...uploaded };
  for (const field of schema.fields) {
    if (field.addOnly && field.multiple) {
      const have = splitList(current[field.key] ?? '');
      const seen = new Set(have.map((value) => normalise(field, value)));
      const added = splitList(uploaded[field.key] ?? '').filter(
        (value) => !seen.has(normalise(field, value))
      );
      values[field.key] = joinList([...have, ...added]);
    }
  }
  return values;
};

/** Fields a row changes that cannot be changed once a record exists. */
const fixedFieldErrors = (
  schema: CsvSchema,
  uploaded: Record<string, string>,
  current: Record<string, string>
): CellError[] =>
  schema.fields.flatMap((field) => {
    const changed =
      normalise(field, uploaded[field.key] ?? '') !== normalise(field, current[field.key] ?? '');
    const reason = field.fixedOnceAdded
      ? `${field.label} can't be changed once added`
      : field.lockedFor?.(current);
    return changed && reason ? [{ key: field.key, message: reason }] : [];
  });

/** The fields that genuinely differ, comparing normalised values. */
const changedFields = (
  schema: CsvSchema,
  uploaded: Record<string, string>,
  current: Record<string, string>
): FieldChange[] =>
  schema.fields
    .filter(
      (field) =>
        normalise(field, uploaded[field.key] ?? '') !== normalise(field, current[field.key] ?? '')
    )
    .map((field) => ({
      key: field.key,
      label: field.label,
      // Shown as written, so users recognise their own values.
      from: current[field.key] ?? '',
      to: uploaded[field.key] ?? ''
    }));

/** Classifies `validation`'s rows against `currentData`. Rows that failed validation keep their errors rather than being dropped. */
export const diffCsv = (
  schema: CsvSchema,
  validation: ValidationResult,
  currentData: Record<string, string>[],
  options: DiffOptions
): DiffResult => {
  const keyOf = (record: Record<string, string>) => recordKey(schema, record);

  // Rows with the same signature import identically, so they are repeats rather than a clash.
  const signatureOf = (values: Record<string, string>) =>
    schema.fields.map((field) => normalise(field, values[field.key] ?? '')).join('\u0000');

  const rowsByKey = new Map<string, ValidatedRow[]>();
  for (const row of validation.rows) {
    const key = keyOf(row.values);
    if (key === '') {
      continue;
    }
    const alreadyOnKey = rowsByKey.get(key);
    if (alreadyOnKey) {
      alreadyOnKey.push(row);
    } else {
      rowsByKey.set(key, [row]);
    }
  }

  /** A repeated line, and the line it repeats. */
  const repeats = new Map<number, number>();
  /** A contested line, and the other lines naming the record it names. */
  const conflicts = new Map<number, number[]>();
  for (const rows of rowsByKey.values()) {
    if (rows.length === 1) {
      continue;
    }
    const first = signatureOf(rows[0].values);
    if (rows.every((row) => signatureOf(row.values) === first)) {
      for (const row of rows.slice(1)) {
        repeats.set(row.line, rows[0].line);
      }
      continue;
    }
    // Hold back every row naming the record: picking one would be a silent guess.
    for (const row of rows) {
      conflicts.set(
        row.line,
        rows.filter((other) => other.line !== row.line).map((other) => other.line)
      );
    }
  }

  const currentByKey = new Map(currentData.map((record) => [keyOf(record), record]));
  const changes: DiffChange[] = [];

  for (const row of validation.rows) {
    const key = keyOf(row.values);

    // Blank keys and clashes are reported as cell errors so they are fixed in place.
    const contestedBy = conflicts.get(row.line);
    const errors: CellError[] = [
      ...row.errors,
      ...(key === ''
        ? [
            {
              key: keyColumns(schema)[0],
              message: `${keyFieldLabels(schema)} identifies a row, so it cannot be left blank`
            }
          ]
        : []),
      ...(contestedBy
        ? [
            {
              key: keyColumns(schema)[0],
              message: keyClashMessage(schema, listLines(contestedBy)),
              duplicate: true as const
            }
          ]
        : [])
    ];

    const repeatOf = repeats.get(row.line);
    const repeat = repeatOf !== undefined ? { repeatOf } : {};
    const current = key === '' ? undefined : currentByKey.get(key);
    if (!current) {
      changes.push({
        classification: 'added',
        key,
        line: row.line,
        values: row.values,
        ...(errors.length > 0 ? { errors } : {}),
        ...repeat,
        ...(contestedBy ? { contestedBy } : {})
      });
      continue;
    }

    if (options.addOnly) {
      changes.push({
        classification: 'unchanged',
        key,
        line: row.line,
        values: row.values,
        current,
        ...repeat
      });
      continue;
    }

    const values = valuesAfterMerge(schema, row.values, current);
    const fieldChanges = changedFields(schema, values, current);
    errors.push(...fixedFieldErrors(schema, values, current));
    changes.push({
      classification: fieldChanges.length > 0 || errors.length > 0 ? 'updated' : 'unchanged',
      key,
      line: row.line,
      values,
      current,
      ...(fieldChanges.length > 0 ? { fieldChanges } : {}),
      ...(errors.length > 0 ? { errors } : {}),
      ...repeat,
      ...(contestedBy ? { contestedBy } : {})
    });
  }

  if (options.treatAbsentAsRemoval) {
    for (const [key, current] of currentByKey) {
      if (!rowsByKey.has(key)) {
        changes.push({ classification: 'removed', key, current });
      }
    }
  }

  return { changes };
};

/**
 * Stable identity for selection and commit. Keyed on line, not contents, so editing a
 * key field doesn't silently deselect the row. Removals have no line, so use the key.
 */
export const changeId = (change: DiffChange): string =>
  change.line === undefined ? `removed:${change.key}` : `row:${change.line}`;

/** Errors other than another row naming the same record, which Conflicts lists. */
export const hasNonDuplicateErrors = (change: DiffChange): boolean =>
  (change.errors ?? []).some((error) => !error.duplicate);

const nonDuplicateErrorLinesByResult = new WeakMap<DiffResult, Set<number>>();

/** Lines whose rows have an error other than naming the same record as another row. */
const linesWithNonDuplicateErrors = (result: DiffResult): Set<number> => {
  let lines = nonDuplicateErrorLinesByResult.get(result);
  if (!lines) {
    lines = new Set(
      result.changes
        .filter((change) => change.line !== undefined && hasNonDuplicateErrors(change))
        .map((change) => change.line as number)
    );
    nonDuplicateErrorLinesByResult.set(result, lines);
  }
  return lines;
};

/**
 * Whether a row names the same record as another importable row. Rows with their own
 * errors are ignored on both sides of the clash.
 */
export const isClashing = (change: DiffChange, result: DiffResult): boolean =>
  change.contestedBy !== undefined &&
  !hasNonDuplicateErrors(change) &&
  change.contestedBy.some((line) => !linesWithNonDuplicateErrors(result).has(line));

/** Whether a clashing row is selected alongside another row it clashes with. */
export const isContested = (
  change: DiffChange,
  selected: ReadonlySet<string>,
  result: DiffResult
): boolean =>
  isClashing(change, result) &&
  selected.has(changeId(change)) &&
  (change.contestedBy ?? []).some(
    (line) => selected.has(`row:${line}`) && !linesWithNonDuplicateErrors(result).has(line)
  );

/** A row's errors that stop it being imported with the rows now selected. */
export const errorsThatBlock = (
  change: DiffChange,
  selected: ReadonlySet<string>,
  result: DiffResult
): CellError[] =>
  (change.errors ?? []).filter(
    (error) => !error.duplicate || isContested(change, selected, result)
  );
